/**
 * Types for Sales Insights (/pc/insights), the rep-facing view of a
 * salesperson's Eclipse book.
 *
 * Access is per user, not per org: a rep stays in their profit-center org and
 * their Clerk user carries `publicMetadata.salesInsights`. The marker is bound
 * to one PC, and only counts while that PC's org is the active one.
 * The user's Clerk org role decides what they see: a member sees their own
 * accounts, an admin sees the whole PC.
 */

import { z } from "zod";

export const salesInsightsUserMetadataSchema = z.object({
  /** Must equal the active PC org's hajocaProfitCenterId */
  hajocaProfitCenterId: z.string().min(1),
  /** Eclipse outside salesperson id, e.g. "TESTREP" */
  osrId: z.string().min(1),
});

export type SalesInsightsUserMetadata = z.infer<
  typeof salesInsightsUserMetadataSchema
>;

/** Who is looking: their Clerk org role, "member" or "admin" */
export type SalesInsightsViewer = "member" | "admin";

/**
 * Which sales an si_accounts row sums: "home" is the PC's only (admins and
 * branch totals), "cooperative" adds its cooperative PCs' (a member's book)
 */
export type SalesInsightsScope = "home" | "cooperative";

/** How an account relates to the signed-in user */
export type AccountAssignment =
  // Credited to the user's own osrId
  | "assigned-to-user"
  // A house account
  | "house"
  // Credited to another rep (only admins see these)
  | "assigned-to-other";

/**
 * The All | Assigned | Unassigned picker's choices, in picker order. Admins
 * get all three; members only get "assigned", which for them is just their own.
 */
export const BOOK_SCOPES = ["all", "assigned", "house"] as const;
export type BookScope = (typeof BOOK_SCOPES)[number];

/** True when an account belongs in a picker choice */
export function inScope(scope: BookScope, assignment: AccountAssignment): boolean {
  // All shows every account the user can see
  if (scope === "all") return true;
  // House (Unassigned) shows only house accounts
  if (scope === "house") return assignment === "house";
  // Assigned shows every account with a rep; a member's book only has their own
  return assignment !== "house";
}

/**
 * Read the marker off a Clerk user's publicMetadata. Returns null when it's
 * absent or malformed, so a bad entry denies access rather than granting it.
 */
export function getSalesInsightsMetadata(user: {
  publicMetadata?: unknown;
}): SalesInsightsUserMetadata | null {
  const raw = (user.publicMetadata as { salesInsights?: unknown } | undefined)
    ?.salesInsights;
  const parsed = salesInsightsUserMetadataSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/**
 * A10's window: the last this-many weeks, compared with the same weeks a year
 * earlier. The loader (scripts/sales-insights/load_tables.py) fills
 * sales_6wk with this window, so change both together.
 */
export const SAME_PERIOD_WEEKS = 6;

/** How many of an account's newest notes the account page shows */
export const SI_NOTES_SHOWN_LIMIT = 5;

/** Longest note allowed, in characters. The si_account_notes CHECK matches. */
export const SI_NOTE_MAX_LENGTH = 2000;

/** POST body for a new note: trimmed, not empty, within the limit */
export const createAccountNoteSchema = z.object({
  body: z.string().trim().min(1).max(SI_NOTE_MAX_LENGTH),
});

/** One note as the account page shows it. createdAt is an ISO timestamp. */
export interface AccountNoteView {
  id: string;
  body: string;
  createdAt: string;
}

/**
 * Response types. Row ids in comments (A1, B3, …) are from the "Data
 * Sources/Definitions (Sales Insights)" doc in the Design Partnership Q3
 * Linear project. Money is in dollars; growth is a fraction (0.05 = +5%), or
 * null when the prior period had no sales. Dates are YYYY-MM-DD.
 */

export interface SalesWithGrowth {
  sales: number;
  growth: number | null;
}

/** Whether a signal is bad news, good news, or neither. */
export type SignalGroup = "risk" | "opportunity" | "steady";

/**
 * The one label shown under an account's name, e.g. "▼ Declining". The page
 * looks up the label, definition and source text by `key`. Which signals
 * exist and how they're decided is ONS-504.
 */
export interface Signal {
  /** Stable id, e.g. "declining" */
  key: string;
  group: SignalGroup;
}

/** One account in the rep's book: a row in the account list. */
export interface SalesInsightsAccountListItem {
  accountId: string;
  name: string;
  /** Which picker choices show this row */
  assignment: AccountAssignment;
  /** A2–A3: this year so far, vs. the same days last year */
  salesYtd: SalesWithGrowth;
  /** A1–A2: this month so far, vs. the same days last year */
  salesMtd: SalesWithGrowth;
  lastOrderDate: string | null;
  /** A4: whole days from the last order to asOf */
  lastOrderDaysAgo: number | null;
  signal: Signal;
}

/** The headline numbers for one picker choice */
export interface BookTotals {
  /** D3: accounts in this choice */
  count: number;
  /** B1: no order in `lapsedDays` or more, largest 12-month sales first */
  lapsed: { count: number; accountNames: string[] };
  /** B2: accounts with sales this month, out of all accounts */
  activeThisMonth: { active: number; total: number };
  /** Like B2, for the year so far: accounts with sales this year, out of all accounts */
  activeThisYear: { active: number; total: number };
  /** B3 */
  salesMtd: SalesWithGrowth;
  /** Like B3, for the year so far vs. the same days last year */
  salesYtd: SalesWithGrowth;
  /** A7, book level: accounts whose first order is on or after `firstOrdersSince` */
  firstOrders: number;
}

/** GET /api/pc/insights: the user's book, their branch, and the account list. */
export interface SalesInsightsBookResponse {
  /** Latest order date in the loaded data; "today" in every definition */
  asOf: string | null;
  /** E1 */
  branch: { name: string; city: string | null; state: string | null } | null;
  /** The PCs whose sales this covers: the user's PC, then its cooperative PCs for a member */
  profitCenters: string[];
  /** E3 */
  rep: { osrId: string; osrName: string | null };
  /** Lapsed means no order in this many days */
  lapsedDays: number;
  /** First orders count from this date (newAccountDays before asOf) */
  firstOrdersSince: string;
  /** Totals per picker choice. Only admins get house and all; an admin's assigned covers every rep. */
  book: { assigned: BookTotals; house?: BookTotals; all?: BookTotals };
  /** E2: year-to-date growth of the rep's assigned accounts vs. the whole branch */
  growthVsBranch: { rep: number | null; branch: number | null; gapPts: number | null };
  accounts: SalesInsightsAccountListItem[];
}

/** GET /api/pc/insights/accounts/:accountId: one account in the rep's book. */
export interface SalesInsightsAccountResponse {
  asOf: string;
  /** D1 */
  accountId: string;
  name: string;
  /** D2 */
  osrId: string | null;
  osrName: string | null;
  /** How this account relates to the signed-in user */
  assignment: AccountAssignment;
  /** D5 */
  paymentTerms: string | null;
  /** A1–A3 */
  salesMtd: SalesWithGrowth;
  salesYtd: SalesWithGrowth;
  /** A4 */
  lastOrder: { date: string; daysAgo: number } | null;
  /** A5: last 12 months */
  ordersPerMonth: number;
  averageOrder: number | null;
  /** A6 */
  channels: { web: number; phone: number; counter: number; other: number };
  /** A7 */
  firstOrderDate: string | null;
  /** A10: the SAME_PERIOD_WEEKS weeks ending asOf, this year and last */
  sameWeeks: { thisYear: number; lastYear: number };
  /** D4: a house account whose YTD growth is at least `threshold` */
  unassignedAndGrowing: { value: boolean; threshold: number };
  /** Data-quality notes keyed by row id */
  flags: Record<string, string>;
  signal: Signal;
}
